import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString, formatToCurrency, queryStringToObject } from '../../utils';

// COMPONENTS
import Button from '../../components/Button';

// ICONS
import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';
import { ReactComponent as TickCircle } from '../../assets/svg/tick-circle.svg';

// TYPES
import {
	DomainPaymentCycle,
	OrganisationPaymentInfoPaymentData,
	OrganisationPaymentInfoPaymentItem,
	OrganisationRegistrationCommand
} from '../../api/models';
import { useHistory } from 'react-router-dom';

interface RegisterOrganisationSummaryTypes {
	// showView: (type: string) => void;
	// organisationDetails: OrganisationRegistrationCommand;
	// setOrganisationDetails: React.Dispatch<React.SetStateAction<OrganisationRegistrationCommand>>;
	// summaryErrorMessage: string;
	// setSummaryErrorMessage: React.Dispatch<React.SetStateAction<string>>;
	// doRegister: (values: OrganisationRegistrationCommand) => Promise<void>;
	// planName: string;
	// planIcon: string;
}

export const RegisterOrganisationSummary: React.FC<RegisterOrganisationSummaryTypes> = (
	{
		// showView,
		// organisationDetails,
		// setOrganisationDetails,
		// summaryErrorMessage,
		// setSummaryErrorMessage,
		// doRegister,
		// planName,
		// planIcon
	}
) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [updatedOrganisationDetails, setUpdatedOrganisationDetails] =
		React.useState<OrganisationPaymentInfoPaymentData>();

	const [monthlyAmount, setMonthlyAmount] = React.useState<string>('');
	const [annualAmount, setAnnualAmount] = React.useState<string>('');
	const [paymentCycle, setPaymentCycle] = React.useState<DomainPaymentCycle>('Monthly');

	const history = useHistory();

	const queryParams = queryStringToObject();
	const organisationId = queryParams.id;

	const goBack = () => {
		history.push(`/register-organisation?id=${organisationId}`);
	};

	const getOrganisationDetails = async () => {
		setIsLoading(true);
		const response = await apibridge.getOrganisationPaymentinfo(organisationId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = {} } = response;
			setIsLoading(false);

			setUpdatedOrganisationDetails(response.result);

			result.payments?.forEach((info: OrganisationPaymentInfoPaymentItem) => {
				if (info.paymentCycle === paymentCycle && info.monthlyAmount !== undefined && info.annualAmount !== undefined) {
					setMonthlyAmount('$' + formatToCurrency(info.monthlyAmount));

					setAnnualAmount('$' + formatToCurrency(info.annualAmount));
				}
			});
		}
	};

	React.useEffect(() => {
		getOrganisationDetails();
	}, []);

	const getPaymentUrl = async (organisationId: string) => {
		setIsLoading(true);

		const response = await apibridge.doRegisterOrganisationPayment(organisationId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			const { result = {} } = response;

			window.location.href = result.url || '';

			setIsLoading(false);
		}
	};

	const updatePaymentCycle = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === 'Monthly' || e.target.value === 'Annually') {
			const cycle = e.target.value;

			setPaymentCycle(cycle);
		}
	};

	const updateAmount = () => {
		updatedOrganisationDetails &&
			updatedOrganisationDetails.payments &&
			updatedOrganisationDetails.payments.forEach((info: OrganisationPaymentInfoPaymentItem) => {
				if (info.paymentCycle === paymentCycle && info.monthlyAmount !== undefined && info.annualAmount !== undefined) {
					setMonthlyAmount('$' + formatToCurrency(info.monthlyAmount));

					setAnnualAmount('$' + formatToCurrency(info.annualAmount));
				}
			});
	};

	React.useEffect(() => {
		updateAmount();
	}, [paymentCycle]);

	const doOrganisationRegistrationPayment = async () => {
		setIsLoading(true);

		const response = await apibridge.postOrganisationPaymentInfo({
			id: organisationId,
			paymentCycle
		});

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			getPaymentUrl(organisationId);

			setIsLoading(false);
		}
	};

	return (
		<div className="register-organisation-summary">
			<Button
				className="btn-text-icon go-back"
				onClick={() => goBack()}
				buttonIcon={<ArrowLeft className="link-icon" />}
			>
				Back
			</Button>

			<div className="section-header">
				<h1 className="h1">Register an organisation</h1>

				<p className="sub-grey-text">Let's go through the payment plan</p>
			</div>

			<div className="section-content">
				<h2 className="h3 summary-intro">
					Here is your payment plan based on a{' '}
					<span>
						<em>{updatedOrganisationDetails && updatedOrganisationDetails.paymentPlanName} plan</em> with{' '}
						<em>{updatedOrganisationDetails && updatedOrganisationDetails.numberOfChildren} children</em>
					</span>
				</h2>
				<div className="row">
					<div className="col col-12 col-md-6 summary-info">
						<ul>
							<li>
								<TickCircle className="icon" /> Assists compliance with the Child Safe Standards
							</li>

							<li>
								<TickCircle className="icon" /> Equips leaders to build a child safe culture
							</li>

							<li>
								<TickCircle className="icon" /> Comprehensive onboarding of staff, volunteers and visitors
							</li>
							<li>
								<TickCircle className="icon" /> Upskills staff to identify and manage risk
							</li>

							<li>
								<TickCircle className="icon" /> Educates and empowers parents and children
							</li>

							<li>
								<TickCircle className="icon" /> Up to date training and resources developed by industry experts
							</li>

							<li>
								<TickCircle className="icon" /> Safe and secure records management
							</li>
						</ul>
					</div>

					<div className="col col-12 col-md-6 summary-cost">
						{updatedOrganisationDetails && updatedOrganisationDetails.icon && (
							<img src={'/assets/' + updatedOrganisationDetails.icon} alt="icon" className="icon" />
						)}

						<h3 className="h3">{updatedOrganisationDetails && updatedOrganisationDetails.paymentPlanName} Plan</h3>

						{paymentCycle !== 'Monthly' ? (
							<p>
								{annualAmount}&nbsp;<span className="sub-grey-text">/year ({monthlyAmount}/month)*</span>
							</p>
						) : (
							<p>
								{monthlyAmount}&nbsp;<span className="sub-grey-text">/month ({annualAmount}/year)*</span>
							</p>
						)}

						<div className="form-group">
							<label className="form-control-radio" key="paymentCycle-monthly">
								<input
									name="paymentCycle"
									type="radio"
									value="Monthly"
									checked={paymentCycle === 'Monthly'}
									onChange={updatePaymentCycle}
								/>
								<span>Billed monthly</span>
							</label>

							<label className="form-control-radio" key="paymentCycle-annually">
								<input
									name="paymentCycle"
									type="radio"
									value="Annually"
									checked={paymentCycle === 'Annually'}
									onChange={updatePaymentCycle}
								/>
								<span>
									Billed annually <em className="sub-grey-text">(Best value)</em>
								</span>
							</label>
						</div>

						<div className="sub-grey-text mtop-24">*Prices above are not inclusive of GST or any other taxes.</div>
					</div>
				</div>{' '}
			</div>

			<div className="section-footer">
				<Button className="btn-primary full-width" disabled={isLoading} onClick={doOrganisationRegistrationPayment}>
					Continue to payment
				</Button>
			</div>
		</div>
	);
};

export default RegisterOrganisationSummary;
